import { useEffect, useRef } from 'react';

/**
 * How a workspace works (ARCHITECTURE.md §10, §14, §65).
 *
 * Written for the person who has just created one and does not yet know what
 * "workspace" means here, or why their connection did not follow them when
 * they switched.
 *
 * Each section answers a question that has a real, surprising answer — the
 * ones a reader could not guess. Restating the obvious would train people to
 * close this dialog unread.
 */

export type WorkspaceHelpDialogProps = {
  open: boolean;
  onClose: () => void;
};

const ROLES = [
  {
    role: 'Owner',
    can: 'Everything, including billing and transferring ownership.',
    note: 'A workspace always keeps at least one owner — the last one cannot be removed or demoted.',
  },
  {
    role: 'Admin',
    can: 'Connect and disconnect platforms, manage members and settings.',
    note: 'Cannot grant or remove ownership. Managing members is deliberately not enough to make someone an owner.',
  },
  {
    role: 'Operator',
    can: 'Watch chat and moderate it.',
    note: 'Cannot change what is connected — the person running a broadcast does not need to reconfigure it mid-show.',
  },
  {
    role: 'Viewer',
    can: 'Watch chat and read settings.',
    note: 'Read-only throughout.',
  },
] as const;

export function WorkspaceHelpDialog({
  open,
  onClose,
}: WorkspaceHelpDialogProps): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="dialog dialog--help"
      aria-labelledby="workspace-help-title"
      onClose={onClose}
      onCancel={onClose}
    >
      <div className="dialog__header">
        <h2 className="dialog__title" id="workspace-help-title">
          How workspaces work
        </h2>
        <button type="button" className="btn btn--ghost" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="dialog__body">
        <h3 className="help__section">What belongs to a workspace</h3>
        <p className="help__para">
          Platform connections, chat history and settings all belong to the <em>workspace</em>,
          never to the person who set them up. Removing a member never takes their connections down
          with them — which is the point, because otherwise losing a staff member would take a
          channel offline mid-broadcast.
        </p>

        <p className="callout">
          Switching workspaces switches everything: a different set of connections, a different chat
          feed, different members. Nothing is shared between them.
        </p>

        <h3 className="help__section">Setting one up</h3>
        <ol className="help__steps">
          <li>
            Name the workspace and set its time zone. Timestamps are stored in UTC and shown in that
            zone.
          </li>
          <li>
            Connect the platforms you broadcast to. Each platform card has its own setup
            instructions.
          </li>
          <li>
            Invite the people who help run your streams, and give each the narrowest role that lets
            them do their job.
          </li>
        </ol>

        <h3 className="help__section">Roles</h3>
        <dl className="help__roles">
          {ROLES.map((entry) => (
            <div key={entry.role} className="help__role">
              <dt>{entry.role}</dt>
              <dd>
                {entry.can}
                <span className="help__role-note">{entry.note}</span>
              </dd>
            </div>
          ))}
        </dl>

        <p className="callout callout--gotcha">
          <strong>Worth knowing:</strong> permissions are enforced by the server on every request,
          not by hiding buttons. A role that cannot connect a platform cannot connect one by any
          means.
        </p>
      </div>

      <div className="dialog__footer">
        <span />
        <button type="button" className="btn btn--primary" onClick={onClose}>
          Done
        </button>
      </div>
    </dialog>
  );
}
